import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function DateRangePicker({ dateRange, onChange, onResetToWorkWeek }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (range, triggerDate, modifiers, e) => {
    // In v9, onSelect can receive (range, triggerDate, modifiers, event)
    // We just need the range object
    if (range) {
      onChange({ from: range.from, to: range.to });
      if (range.from && range.to) {
        setOpen(false);
      }
    } else {
      onChange({ from: undefined, to: undefined });
    }
  };

  const clearDates = (e) => {
    e.stopPropagation();
    onChange({ from: undefined, to: undefined });
  };

  const handleResetToWorkWeek = (e) => {
    e.stopPropagation();
    if (onResetToWorkWeek) {
      onResetToWorkWeek();
    }
    setOpen(false);
  };

  const hasRange = dateRange?.from || dateRange?.to;
  const isDefault = dateRange?.isDefault;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-800 rounded-lg text-xs min-w-[200px] justify-start ${hasRange ? 'border-[#B22234]/60 text-slate-800' : ''}`}
          data-testid="date-range-picker-btn"
        >
          <CalendarIcon className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
          {dateRange?.from ? (
            dateRange?.to ? (
              <span>
                {isDefault && <span className="text-slate-400 mr-1">This Week:</span>}
                {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
              </span>
            ) : (
              <span>{format(dateRange.from, "MMM d, yyyy")} - ...</span>
            )
          ) : (
            <span>Filter by date</span>
          )}
          {hasRange && !isDefault && (
            <X className="h-3 w-3 ml-auto text-slate-400 hover:text-slate-600 flex-shrink-0" onClick={clearDates} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-slate-200 rounded-xl shadow-lg" align="end" data-testid="date-range-calendar">
        <div className="p-2 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500 font-medium px-2">Select Date Range</span>
          {onResetToWorkWeek && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7"
              onClick={handleResetToWorkWeek}
              data-testid="reset-to-work-week-btn"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              This Week
            </Button>
          )}
        </div>
        <Calendar
          mode="range"
          selected={{ from: dateRange?.from, to: dateRange?.to }}
          onSelect={handleSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}
